import {
    assertWixImageResource,
    assertWixVideoInfo,
    WixImageInfo,
    WixImageResource,
    wixImageResourceGetRawImageUrl,
    wixImageResourceGetUrl,
    wixImageResourceIsExternalImage,
    wixImageResourceIsMediaManagerImageInfo,
    wixImageResourceResolveImageAspectRatio,
    wixImageResourceResolveImageWidthHeightRatio,
    WixMediaManagerMedia,
    WixMediaManagerMediaBase,
    WixVideoInfo,
    WixVideoResource
} from "@wix/devzai-common-wix";
import {AspectRatio, assertType, evaluateFunction, FocalPoint, PartiallyRequired} from "@wix/devzai-utils-common";

export type WixMediaResource = WixImageResource | WixVideoResource;

export namespace WixMediaResource {
    export type WithKnownDimensions = WixImageInfo | PartiallyRequired<WixVideoInfo, 'poster'>;
}

export function wixMediaResourceIsSameMedia (mediaResourceA: WixMediaResource, mediaResourceB: WixMediaResource) : boolean {
    if (wixMediaResourceIsWixImageResource(mediaResourceA) && wixMediaResourceIsWixImageResource(mediaResourceB)) {
        return wixImageResourceGetRawImageUrl(mediaResourceA) === wixImageResourceGetRawImageUrl(mediaResourceB);
    }

    if (wixMediaResourceIsWixVideoResource(mediaResourceA) && wixMediaResourceIsWixVideoResource(mediaResourceB)) {
        return mediaResourceA.baseUrl === mediaResourceB.baseUrl;
    }

    return false;
}

export function wixMediaResourceIsWixVideoResource (mediaResource: WixMediaResource) : mediaResource is WixVideoResource {

    if (typeof mediaResource === 'string') {
        return false;
    }

    return (mediaResource as WixVideoInfo).quality !== undefined;
}

export function wixMediaResourceGetFocalPoint (wixMediaResource: WixMediaResource) : FocalPoint | null {
    return (wixMediaResource as WixMediaManagerMediaBase).focalPoint ?? null;
}

export function wixMediaResourceIsWixMediaManagerMedia (wixMediaResource: WixMediaResource) : wixMediaResource is WixMediaManagerMedia.ImageOrVideo {
    return wixMediaResourceIsWixVideoResource(wixMediaResource) || wixImageResourceIsMediaManagerImageInfo(wixMediaResource);
}

export function wixMediaResourceIsWixImageResource (mediaResource: WixMediaResource) : mediaResource is WixImageResource {
    return !wixMediaResourceIsWixVideoResource(mediaResource);
}

export function wixMediaResourceHasKnownDimensions (mediaResource: WixMediaResource) : mediaResource is WixMediaResource.WithKnownDimensions {
    if (wixMediaResourceIsWixImageResource(mediaResource)) {
        return typeof mediaResource === 'object' && 'width' in mediaResource;
    }

    return mediaResource.poster !== undefined;
}

export function wixMediaResourceSupportsFocalPoint (mediaResource: WixMediaResource) : mediaResource is WixMediaResource.WithKnownDimensions & WixMediaManagerMedia.WithFocalPointSupport {
    return wixMediaResourceHasKnownDimensions(mediaResource) && wixMediaResourceIsWixMediaManagerMedia(mediaResource);
}

export function wixMediaResourceResolveMediaAspectRatio<T extends WixMediaResource.WithKnownDimensions> (mediaResource: T) : AspectRatio;
export function wixMediaResourceResolveMediaAspectRatio<T extends WixMediaResource> (mediaResource: T) : AspectRatio | null;
export function wixMediaResourceResolveMediaAspectRatio<T extends WixMediaResource> (mediaResource: T) {
    const result = evaluateFunction(() => {
        if (wixMediaResourceIsWixImageResource(mediaResource)) {
            return wixImageResourceResolveImageAspectRatio(mediaResource);
        } else {

            const poster = mediaResource.poster;
            if (poster) {
                return wixImageResourceResolveImageAspectRatio(poster);
            }

            return null;
        }
    })

    return result as (T extends WixMediaResource.WithKnownDimensions ? AspectRatio : AspectRatio | null)
}

export function wixMediaResourceResolveMediaWidthHeightRatio<T extends WixMediaResource.WithKnownDimensions> (mediaResource: T) : number;
export function wixMediaResourceResolveMediaWidthHeightRatio<T extends WixMediaResource> (mediaResource: T) : number | null;
export function wixMediaResourceResolveMediaWidthHeightRatio<T extends WixMediaResource> (mediaResource: T) {
    const result = evaluateFunction(() => {
        if (wixMediaResourceIsWixImageResource(mediaResource)) {
            return wixImageResourceResolveImageWidthHeightRatio(mediaResource);
        } else {

            const poster = mediaResource.poster;
            if (poster) {
                return wixImageResourceResolveImageWidthHeightRatio(poster);
            }

            return null;
        }
    })

    return result as (T extends WixMediaResource.WithKnownDimensions ? number : number | null)
}

export function assertWixMediaResource (wixMediaResource: WixMediaResource) : asserts wixMediaResource is WixMediaResource;
export function assertWixMediaResource (wixMediaResource: unknown) : asserts wixMediaResource is WixMediaResource;
export function assertWixMediaResource (wixMediaResource: unknown) : asserts wixMediaResource is WixMediaResource {
    assertType<WixMediaResource>(wixMediaResource, assert => {
        assert.isUnion(
            assert => assert.usingAssertionFunction(assertWixVideoInfo),
            assert => assert.usingAssertionFunction(assertWixImageResource),
        )
    })
}

export function wixMediaResourceReplaceExternalImage<V extends WixMediaResource> (
    wixMediaResource: V,
    externalImagesUrlToImageResourceMap: Map<string, WixImageResource>
) {
    if (wixMediaResourceIsWixImageResource(wixMediaResource)) {
        if (wixImageResourceIsExternalImage(wixMediaResource)) {
            const targetImageResource = externalImagesUrlToImageResourceMap.get(wixImageResourceGetUrl(wixMediaResource));

            if (targetImageResource) {
                return targetImageResource
            }
        }
    }

    return wixMediaResource;
}